import * as React from 'react'
import classNames from 'classnames'
import lodash from 'lodash'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { ExecutionElement } from './ExecutionElement'
import { ElementLabel } from './ElementLabel'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import {
    type FileId,
    type FileMetaApp,
    type FileName,
    generateFileId,
} from '../../../shared/domain/files/types.no-deps'
import { SVG_File } from '../svg/SVG_File'
import { getDisplayFileSize } from '../util/fileSize'
import { SVG_Cross } from '../svg/SVG_Cross'
import { SVG_Attach } from '../svg/SVG_Attach'
import { ProgressBar } from '../ui/ProgressBar'
import { type Upload, findUpload, isInvalid } from '../upload/Upload'
import { InvalidUploadErrorMessage } from '../upload/InvalidUploadErrorMessage'
import { Pulse } from '../ui/Pulse'
import {
    attachmentFileExtensions,
    type FileExtension,
    type FileSelectExtension,
    isUnrecognisedExtensionError,
} from '../../../shared/domain/files/fileExtensions.no-deps'
import type { TimelineDetails } from './TimelineDetails'
import { type FileUploadData, isExtensionValid, isFileSizeValid } from '../upload/FileUploadData'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { isNil } from '../../../shared/lib/functional/nil.no-deps'
import { arrayToReadableList } from '../util/string'
import i18next from 'i18next'
import type { PromiseThunkQueue } from '../../../shared/lib/PromiseThunkQueue.no-deps'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    uploadLimit?: number
    timelineDetails: TimelineDetails
    required: boolean
    answerValue: Flows.FilesSelectAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.FilesSelectAnswer>
    uploads: Upload[]
    allowedFileExtensions?: FileSelectExtension[]
    selectFile: (fileExtensions: FileExtension[]) => Promise<FileUploadData[]>
    cancelUpload: (fileId: FileId, logger: ClientLogger) => Promise<void>
    showProgress: boolean
    filesMetaDataMap: Map<FileId, FileMetaApp>
    promiseThunkQueue: PromiseThunkQueue
    openOptions?: () => void
}

interface State {
    invalidFiles: Upload[]
}

export class ElementFilesSelect extends React.Component<Props, State> {
    public state = {
        invalidFiles: [] as Upload[],
    }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    private renderSelectedFile = (fileId: FileId) => {
        const { uploads, showProgress, filesMetaDataMap } = this.props
        const uploadForFile: Upload = findUpload(uploads, fileId)

        // If we don't have any file upload data for this fileId
        if (!uploadForFile) {
            // Check if we got data for this fileId in filesMetaDataMap
            // This will happen when a collaborator is adding file select answers on the report
            // but the file select answers are not in uploads as they are not uploaded by the user
            const fileMetaData = filesMetaDataMap.get(fileId)
            if (fileMetaData) {
                return this.renderFile(
                    fileId,
                    fileMetaData.fileName,
                    fileMetaData.extension,
                    fileMetaData.fileSizeInBytes
                )
            } else {
                return <Pulse key={fileId} />
            }
        }

        const { extension, fileName, fileSizeInBytes } = uploadForFile.fileUploadData

        if (showProgress && uploadForFile.status !== 'Uploaded') {
            let bytesUploaded = 0
            const hasFailed = uploadForFile.status === 'UploadFailed'
            if (uploadForFile.status === 'UploadUploadingFile') bytesUploaded = uploadForFile.bytesUploaded
            const percentComplete = Math.floor((bytesUploaded / fileSizeInBytes) * 100)
            return (
                <div
                    key={fileId}
                    className={classNames('AppCore__flow__ElementFilesSelect__selectedFileProgress', {
                        'AppCore__flow__ElementFilesSelect__selectedFileProgress--failed': hasFailed,
                    })}
                >
                    <div className="AppCore__flow__ElementFilesSelect__selectedFileProgressDetails">
                        <div className="AppCore__flow__ElementFilesSelect__selectedFileProgressName" title={fileName}>
                            {fileName}
                        </div>
                        <div className="AppCore__flow__ElementFilesSelect__selectedFileProgressProgress">
                            {`${percentComplete}%`}
                            {hasFailed && i18next.t('appcore:element.filesSelect.uploadError')}
                        </div>
                        <div className="AppCore__flow__ElementFilesSelect__selectedFileProgressProgressBar">
                            <ProgressBar percentComplete={percentComplete} />
                        </div>
                    </div>
                    <div
                        className="AppCore__flow__ElementFilesSelect__selectedFileProgressRemove"
                        onClick={() => this.handleDeleteClick(fileId)}
                    >
                        <SVG_Cross />
                    </div>
                </div>
            )
        }

        if (uploadForFile.status !== 'UploadInvalid') {
            return this.renderFile(fileId, fileName, extension, fileSizeInBytes)
        }
    }

    private renderFile = (fileId: FileId, fileName: string, extension: FileExtension, fileSizeInBytes: number) => {
        return (
            <div key={fileId} className="AppCore__flow__ElementFilesSelect__selectedFile">
                <div className="AppCore__flow__ElementFilesSelect__selectedFileIcon">
                    <SVG_File extension={extension} />
                </div>
                <div className="AppCore__flow__ElementFilesSelect__selectedFileDetails">
                    <div className="AppCore__flow__ElementFilesSelect__selectedFileName" title={fileName}>
                        {fileName}
                    </div>
                    <div className="AppCore__flow__ElementFilesSelect__selectedFileSize">
                        {getDisplayFileSize(fileSizeInBytes)}
                    </div>
                </div>
                <div
                    className="AppCore__flow__ElementFilesSelect__selectedFileRemove"
                    onClick={() => this.handleDeleteClick(fileId)}
                >
                    <SVG_Cross />
                </div>
            </div>
        )
    }

    private renderInvalidFiles = (uploadForFile: Upload) => {
        const { deps } = this.props
        const { fileId, fileName } = uploadForFile.fileUploadData
        if (isInvalid(uploadForFile)) {
            return (
                <div key={fileId} className="AppCore__flow__ElementFilesSelect__selectedFileInvalid">
                    <div className="AppCore__flow__ElementFilesSelect__selectedFileInvalidDetails">
                        <div className="AppCore__flow__ElementFilesSelect__selectedFileInvalidName" title={fileName}>
                            {fileName}
                        </div>
                        <div className="AppCore__flow__ElementFilesSelect__selectedFileInvalidReason">
                            <InvalidUploadErrorMessage
                                upload={uploadForFile}
                                language={deps.language}
                                TFixed={deps.TFixed}
                            />
                        </div>
                    </div>
                    <div
                        className="AppCore__flow__ElementFilesSelect__selectedFileInvalidRemove"
                        onClick={() => this.removeInvalidFile(fileId)}
                    >
                        <SVG_Cross />
                    </div>
                </div>
            )
        } else return <div></div>
    }

    private addInvalidFiles(invalidFileIds: FileId[]) {
        const { uploads } = this.props
        const existingInvalidFiles = this.state.invalidFiles
        const invalidFiles = invalidFileIds.map((id) => findUpload(uploads, id))
        this.setState({ invalidFiles: existingInvalidFiles.concat(invalidFiles) })
    }

    private removeInvalidFile(invalidFileId: FileId) {
        const existingInvalidFiles = this.state.invalidFiles
        const invalidFiles = existingInvalidFiles.filter((upload) => upload.fileUploadData.fileId !== invalidFileId)
        this.setState({ invalidFiles })
    }

    private addUnrecognisedFile = (error: any) => {
        const allowedExtensions: FileSelectExtension[] = this.getAllowedExtensions()

        if (isUnrecognisedExtensionError(error)) {
            const { filename, extension } = error

            // Creating some fake data for unrecognised file type
            // so we can add it in invalid files and give some feedback to user
            const invalidFileData: Upload = {
                status: 'UploadInvalid',
                fileUploadData: {
                    fileId: generateFileId(),
                    fileName: filename as FileName,
                    createdAt: new Date(),
                    fetchUrl: '',
                    mimeType: '',
                    fileSizeInBytes: 0,
                    extension: extension as FileExtension,
                    status: 'processing',
                },
                canBeDeletedWhenDone: false,
                reason: {
                    type: 'InvalidExtension',
                    validExtensions: allowedExtensions,
                },
            }

            const existingInvalidFiles = this.state.invalidFiles
            this.setState({ invalidFiles: existingInvalidFiles.concat(invalidFileData) })
        }
    }

    private handleAddClick = async () => {
        await this.props.promiseThunkQueue.push(async () => {
            const { createAnswer, selectFile } = this.props
            const allowedExtensions: FileSelectExtension[] = this.getAllowedExtensions()

            try {
                const selectedFilesData = await selectFile(allowedExtensions)

                // Get file upload data for selected files
                const fileUploads: FileUploadData[] = lodash.compact(selectedFilesData)

                // Separate invalid and valid files upload data
                const [invalidFilesData, validFilesData] = lodash.partition(fileUploads, (uploadData) => {
                    return !(
                        isExtensionValid(uploadData, allowedExtensions) ||
                        isFileSizeValid(uploadData, uploadData.fileSizeInBytes)
                    )
                })

                // Invalid file ids
                const invalidFileIds = invalidFilesData.map((invalidData) => invalidData.fileId)

                // Valid file ids
                const validFileIds = validFilesData.map((invalidData) => invalidData.fileId)

                if (invalidFileIds.length) {
                    this.addInvalidFiles(invalidFileIds)
                }

                if (!validFileIds || validFileIds.length === 0) return

                const values = this.props.answerValue ?? []

                await createAnswer((base) => ({
                    ...base,
                    type: 'files-select',
                    value: [...values, ...validFileIds],
                }))
            } catch (error) {
                this.addUnrecognisedFile(error)
            }
        })
    }

    private handleDeleteClick = async (fileId: FileId) => {
        const { cancelUpload, createAnswer, deps } = this.props
        const values = this.props.answerValue ?? []
        await createAnswer((base) => ({
            ...base,
            type: 'files-select',
            value: values.filter((value) => value !== fileId),
        }))

        const logger = deps.createLogger('ElementFilesSelect.handleDeleteClick')
        await cancelUpload(fileId, logger)
    }

    private renderAddFileButton = () => {
        const { values } = this.props.answerValue ?? []
        return (
            <div className="AppCore__flow__ElementFilesSelect__addFileButton" onClick={this.handleAddClick}>
                <div className="AppCore__flow__ElementFilesSelect__addFileButtonItems">
                    <div className="AppCore__flow__ElementFilesSelect__addFileButtonIcon">
                        <SVG_Attach />
                    </div>
                    <div className="AppCore__flow__ElementFilesSelect__addFileButtonText">
                        {values.length > 0
                            ? i18next.t('appcore:element.filesSelect.uploadAnother')
                            : i18next.t('appcore:element.filesSelect.uploadFile')}
                    </div>
                </div>
            </div>
        )
    }

    private getAllowedExtensions = () => {
        const { allowedFileExtensions } = this.props

        const defaultFileSelectExtensions = attachmentFileExtensions

        // If there are no fileTypes set for element, default it to attachmentFileExtensions excluding
        // '.jpg', '.jpeg', '.png' as the are not allowed to be selected in portal UI
        return isNil(allowedFileExtensions)
            ? defaultFileSelectExtensions.filter((extenstion) => !['.jpg', '.jpeg', '.png'].includes(extenstion))
            : allowedFileExtensions
    }

    private renderAllowedTypes = () => {
        const {
            deps: { language, TFixed },
        } = this.props

        const allowedExtensions = this.getAllowedExtensions()

        const fileTypeList = arrayToReadableList(
            allowedExtensions.sort().map((extension) => extension.replace('.', '')),
            TFixed,
            language
        )

        const allowedFileTypesMessage = i18next.t('appcore:element.filesSelect.acceptedFormats', {
            fileTypeList,
        })

        return <div className="AppCore__flow__ElementFilesSelect__allowedTypes">{allowedFileTypesMessage}</div>
    }

    private tryRender = () => {
        const { deps, elementId, label, uploadLimit, timelineDetails, required, validationMessages } = this.props
        const values = this.props.answerValue ?? []
        const invalidFiles = this.state.invalidFiles
        const secondaryLabel: string | undefined = uploadLimit
            ? uploadLimit > 1
                ? i18next.t('appcore:element.filesSelect.limits.other', { uploadLimit })
                : i18next.t('appcore:element.filesSelect.limits.one', { uploadLimit })
            : undefined

        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={this.props.loopPathIds}
                validationMessages={validationMessages}
                timelineDetails={timelineDetails}
            >
                <div className="AppCore__flow__ElementFilesSelect">
                    <ElementLabel
                        required={required}
                        label={label}
                        secondaryLabel={secondaryLabel}
                        hasBeenEdited={timelineDetails && timelineDetails.hasBeenEdited}
                        language={deps.language}
                        validationMessages={validationMessages}
                        openOptions={this.props.openOptions}
                    />
                    <div className="AppCore__flow__ElementFilesSelect__input">
                        {values.map((fileId) => this.renderSelectedFile(fileId))}
                        {invalidFiles.map((invalidFileData) => this.renderInvalidFiles(invalidFileData))}
                        {this.renderAddFileButton()}
                        {this.renderAllowedTypes()}
                    </div>
                </div>
            </ExecutionElement>
        )
    }
}
